// Marketing · Ajustes — una sola fuente de verdad (OrgMarketingSettings) para la aprobación de
// publicaciones y reels, el calendario de contenido, los avisos y el tope diario por campaña.
// GET/PUT /api/v1/admin/marketing/ajustes. Solo una persona admin guarda; el agente recibe 403.
// La primera vez que se guarda se activa Marketing Studio con aprobación obligatoria.

import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { RefreshCw, Save, ShieldCheck } from 'lucide-react';

import { Button, Card, EmptyState, ErrorNote, Loading, PageHeader } from '@/modules/orion-shell/ui';

import { MarketingNoDisponible } from '../api';
import { fetchAjustes, saveAjustes, type Ajustes, type CanalAviso, type Modo } from '../studio';
import { TZ } from '../shared';

type Notificar = (n: { type: string; title: string; message?: string }) => void;

const AVISOS: { key: CanalAviso; label: string }[] = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'whatsapp', label: 'WhatsApp' },
  { key: 'email', label: 'Correo' },
];

const fmt = new Intl.DateTimeFormat('es-MX', { dateStyle: 'medium', timeStyle: 'short', timeZone: TZ });

function aCentavos(pesos: string): number | null | undefined {
  const limpio = pesos.replace(/[$,\s]/g, '');
  if (limpio === '') return null;
  const v = Number(limpio);
  if (!Number.isFinite(v) || v < 0) return undefined;
  return Math.round(v * 100);
}

function Renglon({ titulo, ayuda, children }: { titulo: string; ayuda?: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
      <div className="min-w-0">
        <p className="text-sm font-medium text-foreground">{titulo}</p>
        {ayuda && <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{ayuda}</p>}
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  );
}

function Segmento({ valor, onChange, disabled }: { valor: Modo; onChange: (m: Modo) => void; disabled?: boolean }) {
  const op: { key: Modo; label: string }[] = [
    { key: 'required', label: 'Requiere aprobación' },
    { key: 'auto', label: 'Sale sola' },
  ];
  return (
    <div className="inline-flex rounded-xl border border-border bg-muted p-1" role="radiogroup">
      {op.map((o) => (
        <button
          key={o.key}
          type="button"
          role="radio"
          aria-checked={valor === o.key}
          disabled={disabled}
          onClick={() => onChange(o.key)}
          className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-50 ${
            valor === o.key ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function AjustesView({ addNotification }: { addNotification?: Notificar }) {
  const [base, setBase] = useState<Ajustes | null>(null);
  const [posts, setPosts] = useState<Modo>('required');
  const [reels, setReels] = useState<Modo>('required');
  const [calendario, setCalendario] = useState(true);
  const [avisos, setAvisos] = useState<CanalAviso[]>(['dashboard']);
  const [tope, setTope] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [guardando, setGuardando] = useState(false);
  const [errorGuardar, setErrorGuardar] = useState('');

  const aplicar = (a: Ajustes) => {
    setBase(a);
    // Sin activar, el formulario propone lo que quedará al activar: aprobación obligatoria.
    setPosts(a.enabled ? a.approval_posts : 'required');
    setReels(a.enabled ? a.approval_reels : 'required');
    setCalendario(a.calendar_view_enabled);
    setAvisos(a.enabled ? a.notify_channels : ['dashboard']);
    setTope(a.max_daily_budget_cents == null ? '' : String(a.max_daily_budget_cents / 100));
  };

  const cargar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      aplicar(await fetchAjustes());
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const guardar = async () => {
    setErrorGuardar('');
    const cents = aCentavos(tope);
    if (cents === undefined) {
      setErrorGuardar('El tope diario debe ser un número en pesos (o déjalo vacío).');
      return;
    }
    const activando = !base?.enabled;
    setGuardando(true);
    try {
      const a = await saveAjustes({
        approval_posts: posts,
        approval_reels: reels,
        calendar_view_enabled: calendario,
        notify_channels: avisos,
        max_daily_budget_cents: cents,
      });
      aplicar(a);
      addNotification?.({ type: 'success', title: activando ? 'Marketing Studio activado' : 'Ajustes guardados' });
    } catch (e) {
      setErrorGuardar(e instanceof Error ? e.message : 'No se pudo guardar.');
    } finally {
      setGuardando(false);
    }
  };

  const toggleAviso = (k: CanalAviso) =>
    setAvisos((xs) => (xs.includes(k) ? xs.filter((x) => x !== k) : [...xs, k]));

  if (loading) {
    return (
      <div className="mx-auto w-full max-w-3xl p-4 sm:p-6">
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto w-full max-w-3xl space-y-3 p-4 sm:p-6">
        <PageHeader title="Ajustes de marketing" />
        {error instanceof MarketingNoDisponible ? (
          <EmptyState icon={<ShieldCheck size={18} />} title="Los ajustes de marketing todavía no están disponibles">
            El servidor aún no tiene Marketing Studio.
          </EmptyState>
        ) : (
          <>
            <ErrorNote>{error instanceof Error ? error.message : 'No se pudieron cargar los ajustes.'}</ErrorNote>
            <Button onClick={cargar}>
              <RefreshCw size={14} aria-hidden /> Reintentar
            </Button>
          </>
        )}
      </div>
    );
  }

  const activo = !!base?.enabled;

  return (
    <div className="mx-auto w-full max-w-3xl p-4 sm:p-6">
      <PageHeader
        title="Ajustes de marketing"
        subtitle="Quién aprueba qué antes de publicar. Aplica a todo lo que prepare el agente o tu equipo."
      />

      {!activo && (
        <div className="mb-4 rounded-2xl border border-primary/30 bg-primary/5 px-4 py-4">
          <p className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <ShieldCheck size={16} className="text-primary" aria-hidden /> La aprobación no está activada
          </p>
          <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
            Hoy las publicaciones y los reels salen solos en su fecha. Al guardar, lo que prepare el agente espera en
            Marketing → Por aprobar hasta que un admin lo apruebe. Puedes cambiar cada tipo después.
          </p>
        </div>
      )}

      <Card title="Aprobación" bodyClassName="divide-y divide-border pt-1">
        <Renglon titulo="Publicaciones e historias" ayuda="Feed e historias de Facebook e Instagram.">
          <Segmento valor={posts} onChange={setPosts} disabled={guardando} />
        </Renglon>
        <Renglon titulo="Reels" ayuda="Videos verticales, incluidos los que produce el agente.">
          <Segmento valor={reels} onChange={setReels} disabled={guardando} />
        </Renglon>
        <Renglon titulo="Campañas con gasto" ayuda="Siempre requieren aprobación: no hay modo automático para gastar dinero.">
          <span className="rounded-full bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary">Siempre requiere aprobación</span>
        </Renglon>
      </Card>

      <Card title="Avisos y calendario" className="mt-4" bodyClassName="divide-y divide-border pt-1">
        <Renglon
          titulo="Avisar al aprobador por"
          ayuda="Lo pendiente siempre aparece en Por aprobar. Los avisos por WhatsApp y correo se activan en una próxima actualización."
        >
          <div className="flex flex-wrap gap-2">
            {AVISOS.map((c) => (
              <label key={c.key} className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs text-foreground">
                <input
                  type="checkbox"
                  checked={avisos.includes(c.key)}
                  onChange={() => toggleAviso(c.key)}
                  disabled={guardando}
                  className="accent-primary"
                />
                {c.label}
              </label>
            ))}
          </div>
        </Renglon>
        <Renglon titulo="Mostrar el calendario de contenido" ayuda="Si lo ocultas, las piezas siguen llegando a Por aprobar.">
          <input
            type="checkbox"
            checked={calendario}
            onChange={(e) => setCalendario(e.target.checked)}
            disabled={guardando}
            aria-label="Mostrar el calendario de contenido"
            className="h-5 w-5 accent-primary"
          />
        </Renglon>
      </Card>

      <Card title="Límites" className="mt-4" bodyClassName="pt-1">
        <Renglon titulo="Tope diario por campaña (MXN)" ayuda="Vacío = sin tope diario. El tope mensual está en Presupuesto.">
          <input
            inputMode="decimal"
            value={tope}
            onChange={(e) => setTope(e.target.value)}
            disabled={guardando}
            placeholder="Sin tope"
            aria-label="Tope diario por campaña en pesos"
            className="w-36 rounded-lg border border-border bg-background px-3 py-1.5 text-right text-sm text-foreground"
          />
        </Renglon>
      </Card>

      {errorGuardar && (
        <div className="mt-4">
          <ErrorNote>{errorGuardar}</ErrorNote>
        </div>
      )}

      <div className="mt-5 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-muted-foreground">
          {activo && base?.updated_at
            ? `Versión ${base.version} · guardó ${base.updated_by || 'sin registro'} el ${fmt.format(new Date(base.updated_at))}`
            : 'Solo una persona admin puede guardar estos ajustes.'}
        </p>
        <Button variant="brand" onClick={guardar} disabled={guardando}>
          <Save size={15} aria-hidden /> {activo ? 'Guardar' : 'Activar aprobación'}
        </Button>
      </div>
    </div>
  );
}
